import {
  cacheDecorator,
  invalidateCacheIfSuccessfulDecorator,
  networkFirstAndCacheDecorator,
  updateCache,
} from '@ds/swymkit/tools'

import { swymFetch } from '~/services/fetch-swym'

import type { ISurvey } from '~/entities/content/survey/interfaces/survey'

import type { IUpdateParams } from '~/features-shared/content/api/interfaces/params'
import { contentParser } from '~/features-shared/content/api/parser-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { TagActions } from '~/features-shared/content-derived-type/utils/derived-types'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import type { ICRUDService, ICRUDServiceParams } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

interface ISurveySpecificParams {
  startDate: string
  endDate: string
  startHour: string
  endHour: string
}

/** Parameters to create a survey */
interface ICreationSurveyParams extends Omit<ICRUDServiceParams, 'subject_uri'>, ISurveySpecificParams {
  /** Title of the Survey */
  title?: string
  /** Community of the survey, it will be created in this community */
  community_subject_uri?: SUBJECT_6W_URI
  /** List of questions in survey */
  questions: any[]
  /** If true, the user will be able to access to result right after his vote */
  showResultsAfterVote: boolean
  /** Tags to add / remove */
  taggingActions?: TagActions
  /** Is this survey a draft or not ? */
  published?: boolean
}

/** Parameters to read a survey */
type IReadSurveyParams = Pick<ICRUDServiceParams, 'subject_uri'>

/**
 * Update parameters for Survey
 * @see ICRUDServiceParams
 * @see ICreationSurveyParams
 */
type IUpdateSurveyParams = ICRUDServiceParams
  & Omit<ICreationSurveyParams, 'published' | 'questions' | 'showResultsAfterVote' | keyof ISurveySpecificParams>
  & IUpdateParams
  & Partial<ISurveySpecificParams>

/** Parameters for delete */
interface IDeleteSurveyParams extends ICRUDServiceParams {
  community_subject_uri?: SUBJECT_6W_URI
}

type SurveyCRUDService = ICRUDService<
  ISurvey,
  ICreationSurveyParams,
  IReadSurveyParams,
  IUpdateSurveyParams,
  IDeleteSurveyParams
>

const SURVEY_CREATE_EDIT_TIMEOUT = 60_000

/** A CRUD service for survey */
export const SURVEY_CRUD_API: SurveyCRUDService = {
  create(params, tracker) {
    return swymFetch.post('/api/poll/createpoll', {
      json: {
        params: {
          title: params.title,
          community_id: params.community_subject_uri,
          showResultsAfterVote: params.showResultsAfterVote,
          startDate: params.startDate,
          startHour: params.startHour,
          endDate: params.endDate,
          endHour: params.endHour,
          questions: params.questions,
          published: params.published ? 1 : 0,
          ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
        },
      },
      tracker,
      timeout: SURVEY_CREATE_EDIT_TIMEOUT,
    })
      .json<SwymResponse<ISwymContent>>()
      .then(contentParser<ISurvey>)
      .then(response => updateCache(`survey-${response.result.subject_uri}`, response))
  },

  read(params, tracker) {
    return cacheDecorator(
      `survey-${params.subject_uri}`,
      () => swymFetch
        .post('/api/poll/getpoll', {
          json: {
            params: {
              pollId: params.subject_uri,
            },
          },
          tracker,
        })
        .json<SwymResponse<ISwymContent>>()
        .then(contentParser<ISurvey>)
        .then((response) => {
          response.result.model = IContentModel.Survey
          return response
        }),
    )
  },

  update(params, tracker) {
    return networkFirstAndCacheDecorator(`survey-${params.subject_uri}`, () => swymFetch
      .post('/api/poll/updatepoll', {
        json: {
          params: {
            published: params.published ? 1 : 0,
            title: params.title,
            id: params.subject_uri,
            startDate: params.startDate,
            startHour: params.startHour,
            endDate: params.endDate,
            endHour: params.endHour,
          },
        },
        tracker,
        timeout: SURVEY_CREATE_EDIT_TIMEOUT,
      })
      .json<SwymResponse<ISwymContent>>()
      .then(contentParser<ISurvey>))
  },

  delete(params, tracker) {
    return invalidateCacheIfSuccessfulDecorator(`survey-${params.subject_uri}`, () => swymFetch
      .post('/api/community/deletecontribution', {
        json: {
          params: {
            pollIds: [params.subject_uri],
            community_id: params.community_subject_uri,
          },
        },
        tracker,
      })

      .json<SwymResponse<void>>())
  },
}
